import {useCallback, useEffect, useRef, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import useSavedComponentsState from "../../../hooks/use-saved-components-state";
import {DEFAULT_PRODUCT_CODE_SPLITTER, PRODUCT_CODE_SPLITTER_CACHE_KEY, PRODUCT_CODE_SPLITTER_LIST,} from "./constants";

/**
 * Loads, caches, and updates the product code splitter preference.
 * Supports controlled mode (`value` + `onChange`) or uncontrolled internal state.
 *
 * @param {{ value?: string, onChange?: (value: string) => void }} [options]
 */
export const useProductCodeSplitter = ({value, onChange} = {}) => {
    const isControlled = value !== undefined;
    const valueRef = useRef(value);
    valueRef.current = value;
    const isMounted = useIsMounted();
    const [internalValue, setInternalValue] = useState(DEFAULT_PRODUCT_CODE_SPLITTER);
    const [loading, setLoading] = useState(true);
    const {highestVersion: getCachedSplitter, save: saveCachedSplitter} = useSavedComponentsState({
        name: PRODUCT_CODE_SPLITTER_CACHE_KEY,
        version: '1.0.0',
    });

    const splitter = isControlled ? value : internalValue;

    useEffect(() => {
        let cancelled = false;
        void getCachedSplitter().then((cached) => {
            if (cancelled || !isMounted()) {
                return;
            }
            const cachedValue = cached?.data?.value ?? DEFAULT_PRODUCT_CODE_SPLITTER;
            if (isControlled) {
                if (cachedValue && cachedValue !== valueRef.current) {
                    onChange?.(cachedValue);
                } else if (!valueRef.current) {
                    onChange?.(DEFAULT_PRODUCT_CODE_SPLITTER);
                }
            } else {
                setInternalValue(cachedValue);
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [getCachedSplitter, isControlled, isMounted, onChange]);

    const changeSplitter = useCallback(async (event) => {
        const nextValue = event.target.value;
        const option = PRODUCT_CODE_SPLITTER_LIST.find((entry) => entry.value === nextValue);
        if (!option) {
            return;
        }
        if (!isControlled) {
            setInternalValue(option.value);
        }
        onChange?.(option.value);
        await saveCachedSplitter(option);
    }, [isControlled, onChange, saveCachedSplitter]);

    return {
        splitter: splitter ?? DEFAULT_PRODUCT_CODE_SPLITTER,
        loading,
        changeSplitter,
    };
};
